import type { AcpElicitationOutcome, AcpElicitationSchema } from '@crowdex/agent-contracts';
import type { McpElicitationEvent } from '@crowdex/plugin-contracts';
import { useEffect, useRef, useState } from 'react';
import { AgentElicitationDialog } from '@/components/agent-elicitation-dialog';
import { electronInvoke, isElectronDesktop, listen } from '@/lib/electron-bridge';

/**
 * Global host for MCP server elicitations. It reuses the agent question UI but
 * answers over a separate IPC invocation, allowing the original tool call to
 * remain pending without blocking Electron's response channel.
 */
export function McpElicitationHost() {
  const [pending, setPending] = useState<McpElicitationEvent[]>([]);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    if (!isElectronDesktop()) return;
    let disposed = false;
    let unlisten: (() => void) | undefined;
    void listen<McpElicitationEvent>('mcp-elicitation', ({ payload }) => {
      if (!disposed) {
        setPending((current) =>
          current.some((entry) => entry.requestId === payload.requestId)
            ? current
            : [...current, payload],
        );
      }
    }).then((cleanup) => {
      if (disposed) cleanup();
      else unlisten = cleanup;
    });
    return () => {
      disposed = true;
      unlisten?.();
      for (const request of pendingRef.current) {
        void electronInvoke('mcp_elicitation_respond', {
          requestId: request.requestId,
          result: { action: 'cancel' },
        }).catch(() => undefined);
      }
    };
  }, []);

  const request = pending[0];
  if (!request) return null;
  const answer = (result: AcpElicitationOutcome) => {
    setPending((current) => current.filter((entry) => entry.requestId !== request.requestId));
    void electronInvoke('mcp_elicitation_respond', {
      requestId: request.requestId,
      result,
    }).catch(() => undefined);
  };
  return (
    <AgentElicitationDialog
      key={request.requestId}
      onAnswer={answer}
      pendingCount={pending.length}
      request={{
        requestId: request.requestId,
        sessionId: null,
        toolCallId: null,
        message: request.message,
        mode: 'form',
        requestedSchema: request.requestedSchema as AcpElicitationSchema,
      }}
    />
  );
}
